import { BEACONS, VOICE_PROMPTS, type Beacon, type VoicePrompt } from './prompts.generated';

const DAY_MS = 86_400_000;

/**
 * Strides coprime with the bank sizes, so a full cycle visits every prompt exactly once and two
 * consecutive days never land on neighbours in the pool's order (the pool is grouped by the drive
 * each question reads, so neighbours feel alike). dailyPrompt.test.ts pins the coprimality; if a
 * bank grows past a stride's coprimality, change the stride, not the test.
 */
export const BEACON_STRIDE = 11;
export const VOICE_STRIDE = 7;

export function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Whole days since the Unix epoch for a YYYY-MM-DD date, read as UTC so every reader shares a day. */
export function dayNumber(iso: string): number {
  const t = Date.parse(`${iso}T00:00:00Z`);
  if (Number.isNaN(t)) throw new Error(`dailyPrompt: not a YYYY-MM-DD date: ${iso}`);
  return Math.floor(t / DAY_MS);
}

export function pickForDay<T>(list: readonly T[], day: number, stride: number): T {
  const n = list.length;
  const i = (((day * stride) % n) + n) % n;
  return list[i];
}

export function beaconForDate(iso: string): Beacon {
  return pickForDay(BEACONS, dayNumber(iso), BEACON_STRIDE);
}

export function voicePromptForDate(iso: string): VoicePrompt {
  return pickForDay(VOICE_PROMPTS, dayNumber(iso), VOICE_STRIDE);
}

/**
 * The date the prerendered shell and the first client render agree on. Vite inlines
 * VITE_BUILD_DATE at build time (vite.config.ts), so the HTML written at deploy and the bundle
 * that hydrates it compute the same prompt; the page then swaps to the reader's own day after
 * mount. Dev and tests have no build date and fall back to today on both sides.
 */
export function buildDateIso(): string {
  const raw = (import.meta.env.VITE_BUILD_DATE as string | undefined)?.trim();
  return raw && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : isoDate(new Date());
}

export function formatPromptDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    [x, y] = [y, x % y];
  }
  return x;
}
